/**@jsxImportSource theme-ui */
import React, { useState } from 'react';
import { usePopper } from 'react-popper';
import { usePopover } from './usePopover';
import { PopoverAnchorProps, PopoverContentProps, PopoverProps } from './PopoverTypes';
import { Arrow } from '../Tooltip';
import { elementWasNotClicked, useDetectClickOutsideElement } from './useDetectClickOutsideElement';
import { usePopperModifiers } from './usePopperModifier';

function PopoverAnchor({
    getTriggerCallback,
    setReferenceElement,
    children,
    anchorDisplay,
}: PopoverAnchorProps): JSX.Element {
    return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <div sx={{ display: anchorDisplay }} ref={setReferenceElement} {...getTriggerCallback()}>
            {children}
        </div>
    );
}

function PopoverContent({
    setPopperElement,
    children,
    styles,
    attributes,
    shadow,
    border,
    setArrowElement,
    placement,
    showArrow = false,
    ...otherProps
}: PopoverContentProps) {
    return (
        <div
            id="popper-content"
            ref={setPopperElement}
            sx={{
                zIndex: 999,
                border: border,
                boxShadow: shadow,
                borderRadius: otherProps.borderRadius,
                padding: otherProps.padding,
                background: otherProps.background,
                width: otherProps.width,
                height: otherProps.height,
                maxWidth: otherProps.maxWidth,
                minWidth: otherProps.minWidth,
                maxHeight: otherProps.maxHeight,
                minHeight: otherProps.minHeight,
                margin: otherProps.margin,
                top: `${styles.popper.top}`,
                right: `${styles.popper.right}`,
                left: `${styles.popper.left}`,
                bottom: `${styles.popper.bottom}`,
                transform: `${styles.popper.transform}`,
                position: 'fixed',
            }}
            {...attributes.popper}
        >
            {children}
            {showArrow ? (
                <Arrow
                    style={styles.arrow}
                    setArrowElement={setArrowElement}
                    attributes={attributes.arrow}
                    placement={placement}
                />
            ) : null}
        </div>
    );
}

/**
 * This component feels like it does too much to be helpful / each change we make to this component could be risky.
 */
function Popover({
    anchor,
    trigger,
    offset,
    content,
    placement,
    shadow,
    border,
    open,
    onClose,
    isDismissable,
    matchWidth,
    verticalOverflow,
    anchorDisplay,
    ...otherProps
}: PopoverProps): JSX.Element {
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const { getTriggerCallback } = usePopover({ trigger, setIsOpen });
    const controlledOpen = trigger === 'custom' && typeof open === 'boolean' ? open : isOpen;
    const controlledOnClose = trigger === 'custom' && typeof onClose === 'function' ? onClose : () => setIsOpen(false);
    useDetectClickOutsideElement({
        element: popperElement,
        onClickOutside: (event: MouseEvent) => {
            if (isDismissable && elementWasNotClicked(referenceElement, event)) {
                controlledOnClose();
            }
        },
        enabled: isDismissable,
    });

    const modifiers = usePopperModifiers({
        arrowElement,
        popperElement,
        referenceElement,
        offset,
        matchWidth,
        verticalOverflow,
    });

    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: placement,
        strategy: 'fixed',
        modifiers: modifiers,
    });

    return (
        <>
            <PopoverAnchor
                setReferenceElement={setReferenceElement}
                {...{ getTriggerCallback }}
                anchorDisplay={anchorDisplay}
            >
                {anchor}
            </PopoverAnchor>

            {controlledOpen ? (
                <PopoverContent
                    {...otherProps}
                    {...{ border, shadow, styles, attributes, setArrowElement, placement, setPopperElement }}
                >
                    {content}
                </PopoverContent>
            ) : null}
        </>
    );
}

PopoverAnchor.defaultProps = {
    anchorDisplay: 'inline-block',
};

Popover.defaultProps = {
    border: '1px solid #cccccc',
    shadow: 'sheet',
    placement: 'bottom-start',
    offset: [0, 8],
    background: 'white',
    borderRadius: '8px',
    padding: '16px',
    matchWidth: false,
    isDismissable: false,
    verticalOverflow: false,
};

export default Popover;
